import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useLoadDoctype } from "./useFrappeDoctype";

export const getKdsStationsQueryKey = () => ["kdsStations"];
export const getKdsStationCurrentItemsQueryKey = (stationName) => [
    "kdsStationCurrentItems",
    stationName,
];
export const getKdsItemLabelsQueryKey = (itemCodes) => [
    "kdsItemLabels",
    ...(itemCodes || []),
];

const callKdsStationMethod = async (stationName, method, args) => {
    const params = {
        dt: "KDS Station",
        dn: stationName,
        method,
    };

    if (args && Object.keys(args).length > 0) {
        params.args = JSON.stringify(args);
    }

    const { data } = await axios.get("method/run_doc_method", { params });
    return data?.message;
};

const finishKdsStationItems = async ({ stationName, substation }) => {
    const payload = {
        dt: "KDS Station",
        dn: stationName,
        method: "finish_current_items",
        args: JSON.stringify(
            substation === undefined || substation === null ? {} : { substation }
        ),
    };

    const { data } = await axios.post("method/run_doc_method", payload);
    return data?.message;
};

const fetchKdsStations = async () => {
    const params = {
        fields: JSON.stringify([
            "name",
            "station_name",
            "station_type",
            "capacity",
            "line",
            "substations",
        ]),
        order_by: "creation asc",
        limit_page_length: 0,
    };
    const { data } = await axios.get("resource/KDS Station", { params });
    return Array.isArray(data?.data) ? data.data : [];
};

const fetchKdsItemLabels = async (itemCodes) => {
    if (!Array.isArray(itemCodes) || itemCodes.length === 0) {
        return [];
    }

    const params = {
        fields: JSON.stringify(["name", "item_name"]),
        filters: JSON.stringify([["Item", "name", "in", itemCodes]]),
        limit_page_length: 0,
    };
    const { data } = await axios.get("resource/Item", { params });
    return Array.isArray(data?.data) ? data.data : [];
};

export const useKdsSettings = () => {
    const query = useLoadDoctype("KDS Settings", "KDS Settings");
    const settings = useMemo(() => query?.data?.docs?.[0] || null, [query?.data]);

    return {
        ...query,
        settings,
    };
};

export const useKdsStations = () => {
    return useQuery(getKdsStationsQueryKey(), fetchKdsStations, {
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true,
        cacheTime: 5 * 60 * 1000,
    });
};

export const useKdsStationCurrentItems = (stationName, options = {}) => {
    const { enabled = true, refetchInterval = 5000 } = options;

    return useQuery(
        getKdsStationCurrentItemsQueryKey(stationName),
        () => callKdsStationMethod(stationName, "get_current_items"),
        {
            enabled: !!stationName && enabled,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            refetchOnMount: true,
            refetchInterval: stationName && enabled ? refetchInterval : false,
        }
    );
};

export const useFinishKdsStationItems = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ stationName, substation }) =>
            finishKdsStationItems({ stationName, substation }),
        {
            onSuccess: (_data, variables) => {
                if (!variables?.stationName) return;

                const currentItemsKey = getKdsStationCurrentItemsQueryKey(
                    variables.stationName
                );
                queryClient.invalidateQueries(currentItemsKey);
                queryClient.refetchQueries(currentItemsKey);
                queryClient.invalidateQueries(getKdsStationsQueryKey());
            },
        }
    );
};

export const useKdsItemLabels = (itemCodes) => {
    const normalizedCodes = useMemo(
        () =>
            Array.from(new Set((itemCodes || []).map(String).filter(Boolean))).sort(),
        [itemCodes]
    );

    return useQuery(
        getKdsItemLabelsQueryKey(normalizedCodes),
        () => fetchKdsItemLabels(normalizedCodes),
        {
            enabled: normalizedCodes.length > 0,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            cacheTime: 5 * 60 * 1000,
        }
    );
};
